import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import {
  Download,
  Send,
  CheckCircle,
  XCircle,
  Edit,
  Package,
  DollarSign,
  FileText,
  Building2,
  User,
  Calendar,
  Truck,
  Mail,
  Copy,
  Archive,
  MessageSquare
} from 'lucide-react';
import { procurementService, PurchaseOrder } from '../../../src/services/procurement.service';
import { poPDFGenerator } from '../../../src/utils/po-pdf-generator';
import { showSuccess, showError } from '../../../src/utils/notification.utils';

interface PurchaseOrderDetailViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  onEdit?: () => void;
  onDuplicate?: (order: PurchaseOrder) => void;
  onRefresh?: () => void;
}

export function PurchaseOrderDetailView({
  open,
  onOpenChange,
  orderId,
  onEdit,
  onDuplicate,
  onRefresh
}: PurchaseOrderDetailViewProps) {
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<PurchaseOrder | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Format currency with commas
  const formatCurrency = (value: number | string | undefined): string => {
    if (value === undefined || value === null) return '0.00';
    const num = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  useEffect(() => {
    if (open && orderId) {
      loadOrder();
    }
  }, [open, orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      console.log('[PurchaseOrderDetailView] Loading order:', orderId);
      const response = await procurementService.getPurchaseOrder(orderId);

      console.log('[PurchaseOrderDetailView] Response:', response);
      console.log('[PurchaseOrderDetailView] Response.data:', response.data);
      console.log('[PurchaseOrderDetailView] Response.data.items:', response.data?.items);

      if (response.success && response.data) {
        // Handle nested data structure from backend
        const orderData = (response.data as any).data || response.data;
        console.log('[PurchaseOrderDetailView] Extracted order data:', orderData);
        console.log('[PurchaseOrderDetailView] Order items:', orderData?.items);

        // If items are not included or empty, try fetching them separately
        if (!orderData.items || orderData.items.length === 0) {
          console.log('[PurchaseOrderDetailView] Items not found, fetching separately...');
          try {
            const itemsResponse = await procurementService.getPurchaseOrderItems(orderId);
            console.log('[PurchaseOrderDetailView] Items response:', itemsResponse);

            if (itemsResponse.success && itemsResponse.data) {
              const items = (itemsResponse.data as any).data || itemsResponse.data;
              console.log('[PurchaseOrderDetailView] Extracted items:', items);
              orderData.items = Array.isArray(items) ? items : [];
            }
          } catch (itemsError: any) {
            console.error('[PurchaseOrderDetailView] Failed to fetch items:', itemsError);
            // Continue without items rather than failing completely
          }
        }

        // If vendor information is missing, fetch it separately
        if (orderData.vendor_id && (!orderData.vendor_name || !orderData.vendor_address)) {
          console.log('[PurchaseOrderDetailView] Vendor info missing, fetching vendor:', orderData.vendor_id);
          try {
            const vendorResponse = await procurementService.getVendor(orderData.vendor_id);
            console.log('[PurchaseOrderDetailView] Vendor response:', vendorResponse);

            if (vendorResponse.success && vendorResponse.data) {
              const vendor = (vendorResponse.data as any).data || vendorResponse.data;
              console.log('[PurchaseOrderDetailView] Extracted vendor:', vendor);
              orderData.vendor_name = vendor.vendor_name || orderData.vendor_name;
              orderData.vendor_address = vendor.address || orderData.vendor_address;
              orderData.vendor_contact = vendor.contact_person || vendor.email || vendor.phone || orderData.vendor_contact;
            }
          } catch (vendorError: any) {
            console.error('[PurchaseOrderDetailView] Failed to fetch vendor:', vendorError);
            // Continue without vendor details
          }
        }

        // If vendor_contact_id exists, fetch contact details
        if (orderData.vendor_contact_id) {
          console.log('[PurchaseOrderDetailView] Loading vendor contact:', orderData.vendor_contact_id);
          try {
            const contactResponse = await procurementService.getVendorContact(orderData.vendor_contact_id);
            console.log('[PurchaseOrderDetailView] Contact response:', contactResponse);

            if (contactResponse.success && contactResponse.data) {
              const contact = (contactResponse.data as any).data || contactResponse.data;
              console.log('[PurchaseOrderDetailView] Extracted contact:', contact);
              orderData.vendor_contact_person = contact.contact_name || orderData.vendor_contact_person;
              orderData.vendor_contact_email = contact.email || orderData.vendor_contact_email;
              orderData.vendor_contact_phone = contact.phone || orderData.vendor_contact_phone;
            }
          } catch (contactError: any) {
            console.error('[PurchaseOrderDetailView] Failed to fetch contact:', contactError);
            // Continue without contact details
          }
        }

        setOrder(orderData);
      } else {
        showError({
          title: 'Load Failed',
          message: 'Failed to load purchase order details'
        });
      }
    } catch (error: any) {
      console.error('[PurchaseOrderDetailView] Error:', error);
      showError({
        title: 'Load Failed',
        message: error?.message || 'Failed to load purchase order'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!order) return;

    try {
      setActionLoading('download');
      await poPDFGenerator.generatePO(order);
      showSuccess({
        title: 'PDF Downloaded',
        message: 'Purchase order PDF has been downloaded'
      });
    } catch (error: any) {
      showError({
        title: 'Download Failed',
        message: error?.message || 'Failed to generate PDF'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async () => {
    if (!order) return;

    try {
      setActionLoading('approve');
      const response = await procurementService.approvePurchaseOrder(order.po_id);

      if (response.success) {
        showSuccess({
          title: 'PO Approved',
          message: 'Purchase order has been approved'
        });
        loadOrder();
        onRefresh?.();
      }
    } catch (error: any) {
      showError({
        title: 'Approval Failed',
        message: error?.message || 'Failed to approve purchase order'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSendToVendor = async () => {
    if (!order) return;

    try {
      setActionLoading('send');
      const response = await procurementService.sendPurchaseOrderToVendor(order.po_id);

      if (response.success) {
        showSuccess({
          title: 'PO Sent',
          message: 'Purchase order has been sent to vendor'
        });
        loadOrder();
        onRefresh?.();
      }
    } catch (error: any) {
      showError({
        title: 'Send Failed',
        message: error?.message || 'Failed to send purchase order'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!order) return;

    try {
      setActionLoading('cancel');
      const response = await procurementService.cancelPurchaseOrder(order.po_id);

      if (response.success) {
        showSuccess({
          title: 'PO Cancelled',
          message: 'Purchase order has been cancelled'
        });
        loadOrder();
        onRefresh?.();
      }
    } catch (error: any) {
      showError({
        title: 'Cancellation Failed',
        message: error?.message || 'Failed to cancel purchase order'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAcknowledge = async () => {
    if (!order) return;

    try {
      setActionLoading('acknowledge');
      const response = await procurementService.acknowledgePurchaseOrder(order.po_id);

      if (response.success) {
        showSuccess({
          title: 'PO Acknowledged',
          message: 'Purchase order has been marked as acknowledged'
        });
        loadOrder();
        onRefresh?.();
      }
    } catch (error: any) {
      showError({
        title: 'Acknowledge Failed',
        message: error?.message || 'Failed to acknowledge purchase order'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDuplicate = () => {
    if (!order || !onDuplicate) return;
    onDuplicate(order);
    onOpenChange(false);
  };

  const handleArchive = async () => {
    if (!order) return;

    try {
      setActionLoading('archive');
      const response = await procurementService.cancelPurchaseOrder(order.po_id, 'Archived by user');

      if (response.success) {
        showSuccess({
          title: 'PO Archived',
          message: 'Purchase order has been archived'
        });
        loadOrder();
        onRefresh?.();
      }
    } catch (error: any) {
      showError({
        title: 'Archive Failed',
        message: error?.message || 'Failed to archive purchase order'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { variant: any; icon: any; label: string }> = {
      draft: { variant: 'secondary', icon: FileText, label: 'Draft' },
      pending_approval: { variant: 'default', icon: Calendar, label: 'Pending Approval' },
      approved: { variant: 'default', icon: CheckCircle, label: 'Approved' },
      sent: { variant: 'default', icon: Send, label: 'Sent to Vendor' },
      acknowledged: { variant: 'default', icon: Mail, label: 'Acknowledged' },
      partially_received: { variant: 'default', icon: Package, label: 'Partially Received' },
      completed: { variant: 'default', icon: CheckCircle, label: 'Completed' },
      cancelled: { variant: 'destructive', icon: XCircle, label: 'Cancelled' },
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading || !order) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[900px]">
          <DialogHeader>
            <DialogTitle>Loading Purchase Order</DialogTitle>
            <DialogDescription>Please wait while we fetch the purchase order details...</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1100px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6" />
              <div>
                <DialogTitle>Purchase Order {order.po_number}</DialogTitle>
                <DialogDescription>
                  Created on {new Date(order.created_at).toLocaleDateString()}
                </DialogDescription>
              </div>
            </div>
            {getStatusBadge(order.status)}
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={!!actionLoading}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>

            {order.status === 'draft' && onEdit && (
              <Button variant="outline" size="sm" onClick={onEdit} disabled={!!actionLoading}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}

            {order.status === 'pending_approval' && (
              <>
                <Button
                  size="sm"
                  onClick={handleApprove}
                  disabled={actionLoading === 'approve'}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEdit}
                  disabled={!!actionLoading}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Request Revision
                </Button>
              </>
            )}

            {(order.status === 'approved' || order.status === 'pending_approval') && (
              <Button
                size="sm"
                onClick={handleSendToVendor}
                disabled={actionLoading === 'send'}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="h-4 w-4 mr-2" />
                Send to Vendor
              </Button>
            )}

            {order.status === 'sent' && (
              <Button
                size="sm"
                onClick={handleAcknowledge}
                disabled={actionLoading === 'acknowledge'}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark as Acknowledged
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={handleDuplicate}
              disabled={actionLoading === 'duplicate'}
            >
              <Copy className="h-4 w-4 mr-2" />
              Duplicate PO
            </Button>

            {order.status !== 'cancelled' && order.status !== 'completed' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleArchive}
                disabled={actionLoading === 'archive'}
                className="text-orange-600 hover:text-orange-700 border-orange-300"
              >
                <Archive className="h-4 w-4 mr-2" />
                Archive PO
              </Button>
            )}
          </div>

          <Separator />

          {/* Vendor and Project Information */}
          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Vendor Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Vendor Name</p>
                  <p className="font-medium">{order.vendor_name || 'Not specified'}</p>
                </div>
                {order.vendor_address && (
                  <div>
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="text-sm">{order.vendor_address}</p>
                  </div>
                )}
                {order.vendor_contact && (
                  <div>
                    <p className="text-sm text-muted-foreground">Contact</p>
                    <p className="text-sm">{order.vendor_contact}</p>
                  </div>
                )}
                {!order.vendor_address && !order.vendor_contact && (
                  <div className="text-sm text-muted-foreground italic">
                    Additional vendor details not available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Order Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Created By</p>
                  <p className="font-medium">{order.created_by_name || order.created_by || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Order Date</p>
                  <p className="text-sm">{order.po_date ? new Date(order.po_date).toLocaleDateString() : 'N/A'}</p>
                </div>
                {(order.requested_delivery_date || order.required_date) && (
                  <div>
                    <p className="text-sm text-muted-foreground">Required Date</p>
                    <p className="text-sm">{new Date(order.requested_delivery_date || order.required_date).toLocaleDateString()}</p>
                  </div>
                )}
                {(order as any).priority && (
                  <div>
                    <p className="text-sm text-muted-foreground">Priority</p>
                    <p className="text-sm capitalize">{(order as any).priority}</p>
                  </div>
                )}
                {(order as any).department && (
                  <div>
                    <p className="text-sm text-muted-foreground">Department</p>
                    <p className="text-sm">{(order as any).department}</p>
                  </div>
                )}
                {(order as any).requisition_number && (
                  <div>
                    <p className="text-sm text-muted-foreground">Requisition #</p>
                    <p className="text-sm">{(order as any).requisition_number}</p>
                  </div>
                )}
                {order.project_name && (
                  <div>
                    <p className="text-sm text-muted-foreground">Project</p>
                    <p className="text-sm">{order.project_name}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Delivery Information */}
          <Separator />
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Delivery Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(order.delivery_address || order.shipping_method || (order as any).freight_terms || (order as any).partial_delivery !== undefined) ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    {((order as any).ship_to_address || order.delivery_address) && (
                      <div>
                        <p className="text-sm text-muted-foreground">Delivery Address</p>
                        <p className="text-sm">
                          {(order as any).ship_to_address ? (
                            <>
                              {(order as any).ship_to_address.street && <>{(order as any).ship_to_address.street}<br /></>}
                              {(order as any).ship_to_address.city && <>{(order as any).ship_to_address.city}, </>}
                              {(order as any).ship_to_address.state && <>{(order as any).ship_to_address.state} </>}
                              {(order as any).ship_to_address.zip && <>{(order as any).ship_to_address.zip}<br /></>}
                              {(order as any).ship_to_address.country && <>{(order as any).ship_to_address.country}</>}
                            </>
                          ) : order.delivery_address}
                        </p>
                      </div>
                    )}
                    {order.shipping_method && (
                      <div>
                        <p className="text-sm text-muted-foreground">Shipping Method</p>
                        <p className="text-sm capitalize">{order.shipping_method.replace('_', ' ')}</p>
                      </div>
                    )}
                    {(order as any).shipping_terms && (
                      <div>
                        <p className="text-sm text-muted-foreground">Shipping Terms</p>
                        <p className="text-sm">{(order as any).shipping_terms}</p>
                      </div>
                    )}
                    {(order as any).partial_delivery !== undefined && (
                      <div>
                        <p className="text-sm text-muted-foreground">Partial Delivery</p>
                        <p className="text-sm">{(order as any).partial_delivery ? 'Allowed' : 'Not Allowed'}</p>
                      </div>
                    )}
                  </div>

                  {(order as any).delivery_terms && (
                    <div>
                      <p className="text-sm text-muted-foreground">Delivery Terms</p>
                      <p className="text-sm whitespace-pre-wrap">{(order as any).delivery_terms}</p>
                    </div>
                  )}

                  {(order as any).delivery_instructions && (
                    <div>
                      <p className="text-sm text-muted-foreground">Delivery Instructions</p>
                      <p className="text-sm whitespace-pre-wrap">{(order as any).delivery_instructions}</p>
                    </div>
                  )}

                  {((order as any).vendor_contact_person || (order as any).vendor_contact_email || (order as any).vendor_contact_phone) && (
                    <>
                      <Separator />
                      <div className="grid grid-cols-2 gap-4">
                        {(order as any).vendor_contact_person && (
                          <div>
                            <p className="text-sm text-muted-foreground">Vendor Contact Person</p>
                            <p className="text-sm">{(order as any).vendor_contact_person}</p>
                          </div>
                        )}
                        {(order as any).vendor_contact_email && (
                          <div>
                            <p className="text-sm text-muted-foreground">Vendor Email</p>
                            <p className="text-sm">{(order as any).vendor_contact_email}</p>
                          </div>
                        )}
                        {(order as any).vendor_contact_phone && (
                          <div>
                            <p className="text-sm text-muted-foreground">Vendor Phone</p>
                            <p className="text-sm">{(order as any).vendor_contact_phone}</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="text-sm text-muted-foreground italic">
                  No delivery information provided
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Separator />
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="h-4 w-4" />
                Line Items ({order.items?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {order.items && order.items.length > 0 ? (
                <div className="space-y-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item Name</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead>UOM</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {order.items.map((item) => {
                        // Calculate line total if total_amount is 0 or missing
                        const lineTotal = Number(item.total_amount || 0) > 0
                          ? Number(item.total_amount)
                          : Number(item.quantity_ordered || 0) * Number(item.unit_price || 0);

                        return (
                          <TableRow key={item.po_item_id}>
                            <TableCell className="align-top">
                              <div>
                                <p className="font-medium">{item.item_name || 'N/A'}</p>
                                {item.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                                )}
                                {((item as any).manufacturer || (item as any).model_number) && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {(item as any).manufacturer && `Mfr: ${(item as any).manufacturer}`}
                                    {(item as any).manufacturer && (item as any).model_number && ' | '}
                                    {(item as any).model_number && `Model: ${(item as any).model_number}`}
                                  </p>
                                )}
                                {(item as any).line_notes && (
                                  <p className="text-xs italic text-muted-foreground mt-1">
                                    Note: {(item as any).line_notes}
                                  </p>
                                )}
                                {item.requested_delivery_date && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Required: {new Date(item.requested_delivery_date).toLocaleDateString()}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium align-top text-muted-foreground">{item.item_code || 'N/A'}</TableCell>
                            <TableCell className="text-right align-top">{item.quantity_ordered}</TableCell>
                            <TableCell className="align-top">{item.unit_of_measure || 'each'}</TableCell>
                            <TableCell className="text-right align-top">${formatCurrency(item.unit_price)}</TableCell>
                            <TableCell className="text-right font-medium align-top">${formatCurrency(lineTotal)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No line items found</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Financial Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">${formatCurrency(order.subtotal)}</span>
                </div>

                {(Number((order as any).discount_amount || 0) > 0 || Number((order as any).discount_percentage || 0) > 0) && (
                  <div className="flex justify-between text-red-600">
                    <span>
                      Discount
                      {Number((order as any).discount_percentage || 0) > 0 && ` (${(order as any).discount_percentage}%)`}
                    </span>
                    <span>-${formatCurrency((order as any).discount_amount)}</span>
                  </div>
                )}

                {Number(order.tax_amount || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Tax
                      {Number((order as any).tax_rate || 0) > 0 && ` (${(order as any).tax_rate}%)`}
                    </span>
                    <span className="font-medium">${formatCurrency(order.tax_amount)}</span>
                  </div>
                )}

                {Number(order.shipping_amount || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="font-medium">${formatCurrency(order.shipping_amount)}</span>
                  </div>
                )}

                {Number((order as any).freight_amount || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Freight</span>
                    <span className="font-medium">${formatCurrency((order as any).freight_amount)}</span>
                  </div>
                )}

                {Number((order as any).other_charges || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Other Charges</span>
                    <span className="font-medium">${formatCurrency((order as any).other_charges)}</span>
                  </div>
                )}
              </div>

              <Separator />

              <div className="flex justify-between text-lg font-bold">
                <span>Total Amount</span>
                <span className="text-primary">${formatCurrency(order.total_amount)}</span>
              </div>

              {(order.payment_terms || (order as any).payment_method) && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    {order.payment_terms && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Payment Terms</span>
                        <span>{order.payment_terms}</span>
                      </div>
                    )}
                    {(order as any).payment_method && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Payment Method</span>
                        <span>{(order as any).payment_method}</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {(Number((order as any).retention_percentage || 0) > 0 || Number((order as any).retention_amount || 0) > 0) && (
                <>
                  <Separator />
                  <div className="flex justify-between text-sm text-orange-600">
                    <span>
                      Retention
                      {Number((order as any).retention_percentage || 0) > 0 && ` (${(order as any).retention_percentage}%)`}
                    </span>
                    <span className="font-medium">${formatCurrency((order as any).retention_amount)}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Terms & Conditions */}
          {((order as any).warranty_terms || (order as any).terms_and_conditions || (order as any).special_instructions) && (
            <>
              <Separator />
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Terms & Conditions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(order as any).warranty_terms && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Warranty Terms</p>
                      <p className="text-sm whitespace-pre-wrap">{(order as any).warranty_terms}</p>
                    </div>
                  )}
                  {(order as any).terms_and_conditions && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Terms & Conditions</p>
                      <p className="text-sm whitespace-pre-wrap">{(order as any).terms_and_conditions}</p>
                    </div>
                  )}
                  {(order as any).special_instructions && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Special Instructions</p>
                      <p className="text-sm whitespace-pre-wrap">{(order as any).special_instructions}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Description/Notes */}
          {order.description && (
            <>
              <Separator />
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{order.description}</p>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
